import React, { useRef, useState } from 'react';
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { importEmailTemplate } from '../../../API/PhishingAPI';

export function ImportEmailTemplateModal({ onSubmit, onImported, open = false, showImportEmailButton = true, disabled, onBusyChange, t }) {
  const [isOpen, setOpen] = useState(open);
  const [content, setContent] = useState('');
  const [convertLinks, setConvertLinks] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  function close() { if (!pending.current && !reading) { setOpen(false); setError(''); } }

  function readFile(event) {
    const file = event.target.files[0];
    if (!file) return;
    setError(''); setReading(true);
    const reader = new FileReader();
    reader.onload = () => { setContent(String(reader.result)); setReading(false); };
    reader.onerror = () => { setError(t('template_import.file_error')); setReading(false); };
    reader.readAsText(file);
  }

  async function submit() {
    if (pending.current || reading || !content.trim()) return;
    pending.current = true; setBusy(true); setError(''); onBusyChange?.(true);
    try {
      const result = await importEmailTemplate({ content, convert_links: convertLinks });
      if (onImported) await onImported(result);
      else await onSubmit(result.html, result.subject, result.text);
      setOpen(false);
    } catch (failure) {
      setError(failure.message || t('template_import.email_error'));
    } finally {
      pending.current = false; setBusy(false); onBusyChange?.(false);
    }
  }

  return <>
    {showImportEmailButton && <Button color="primary" outline className="mb-3" disabled={disabled}
      onClick={() => { setError(''); setOpen(true); }}>{t('email_template.import_email.title')}</Button>}
    <Modal isOpen={isOpen} toggle={close} zIndex={1080} backdrop={busy || reading ? 'static' : true}>
      <ModalHeader toggle={busy || reading ? undefined : close}>{t('email_template.import_email.title')}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" role="alert">{error}</Alert>}
        <Label for="import-email-file">{t('template_import.eml_file')}</Label>
        <Input id="import-email-file" type="file" accept=".eml,message/rfc822" disabled={busy || reading} onChange={readFile} />
        <Label for="import-email-content" className="mt-3">{t('template_import.raw_email')}</Label>
        <Input id="import-email-content" type="textarea" rows="10" value={content} disabled={busy || reading}
          onChange={event => setContent(event.target.value)} />
        <Label check className="ml-4 mt-3">
          <Input type="checkbox" checked={convertLinks} disabled={busy || reading}
            onChange={event => setConvertLinks(event.target.checked)} />
          {t('email_template.import_email.convert_links')}
        </Label>
      </ModalBody>
      <ModalFooter>
        <Button color="link" disabled={busy || reading} onClick={close}>{t('email_template.actions.cancel')}</Button>
        <Button color="primary" disabled={busy || reading || !content.trim()} onClick={submit}>
          {(busy || reading) && <Spinner tag="span" size="sm" className="mr-2" />}
          {t(busy ? 'template_import.importing' : 'template_import.import_email')}
        </Button>
      </ModalFooter>
    </Modal>
  </>;
}

export default withTranslation()(ImportEmailTemplateModal);
